import Badge, { BadgeColor } from "@/components/Badge";
import Icon from "@/components/Icon";
import type { MaterialNames } from "@/components/material_symbol_names";
import { useFlags } from "@/hooks/useFeatureFlags";
import useAmplitude from "@/hooks/useAmplitude";
import { canApplyStoredDefaultTab, clearDefaultTab, getDefaultTab, setDefaultTab, tabLocation } from "@/lib/defaultTab";
import { trackDefaultTabCleared, trackDefaultTabSaved } from "@/lib/defaultTabAmplitude";
import { Path, useNavigate } from "@/router";
import { PropsWithChildren, ReactNode, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { LitPrimaryTab } from "./LitTabs";

export interface TabItem {
  label: string;
  active?: boolean;
  onClick?(): void;
  link?: Path;
  badgeValue?: number;
  badgeColor?: BadgeColor;
  iconName?: MaterialNames;
  icon?: ReactNode;
  iconOnly?: boolean;
  testId?: string;
}

/**
 * Props for the Tabs component
 * @property {string} [className] - Optional className for additional styling
 * @property {Array} tabItems - Array of tab item configurations
 * @property {string} tabItems[].label - The label text for the tab
 * @property {MaterialNames} [tabItems[].iconName] - Optional Material icon beside the label, or alone when `iconOnly` is true
 * @property {ReactNode} [tabItems[].icon] - Optional custom icon node beside the label, or alone when `iconOnly` is true
 * @property {boolean} [tabItems[].iconOnly] - When true with `iconName` or `icon`, shows only the icon (label remains for `aria-label`)
 * @property {string} [tabItems[].testId] - Optional `data-testid` for the tab button (defaults to `"tab"`)
 * @property {boolean} [tabItems[].active] - Whether the tab is active
 * @property {Function} [tabItems[].onClick] - Optional click handler
 * @property {Path} [tabItems[].link] - Optional navigation path
 * @property {number} [tabItems[].badgeValue] - Optional badge value to display on the tab (including icon-only tabs). When provided, shows a label badge with the number. If only badgeColor is set, shows a dot badge.
 * @property {BadgeColor} [tabItems[].badgeColor] - Optional badge color. When set, shows a badge (dot if no value, label if value provided). Defaults to primary.
 * @property actionIcon - Optional slot on the right of the tab row. Pad only this cluster (`pb-6`) so the indicator stays on the divider. Do not put top-level index create buttons here — those belong on TableControls.actions.
 */
export interface TabsProps {
  className?: string;
  tabItems: TabItem[];
  actionIcon?: ReactNode;
  showWhenSingle?: boolean;
}

const isTabActive = (currentPath: string, linkTo?: Path, active?: boolean) => {
  if (!linkTo || typeof active === "boolean") {
    return active;
  }

  let path = currentPath;

  while (path && path !== "/") {
    if (matchPath(linkTo, path)) {
      return true;
    }

    path = path.split("/").slice(0, -1).join("/");
  }

  return false;
};

function isExactTabPath(currentPath: string, linkTo?: Path): boolean {
  if (!linkTo) {
    return true;
  }

  const path = currentPath.replace(/\/$/, "") || "/";
  return Boolean(matchPath({ path: String(linkTo), end: true }, path));
}

function defaultTabTitle(isSavedDefault: boolean, label: string): string {
  return isSavedDefault ? `Remove ${label} as your default tab` : `Make ${label} your default tab`;
}

function DefaultTabMarker({
  isSavedDefault,
  label,
  showActionTitle
}: {
  isSavedDefault: boolean;
  label: string;
  showActionTitle: boolean;
}) {
  const title = showActionTitle ? defaultTabTitle(isSavedDefault, label) : undefined;

  return (
    <span
      title={title}
      data-testid="default-tab-action"
      aria-hidden="true"
      className="inline-flex shrink-0 items-center"
    >
      <Icon name="star" size={16} filled={isSavedDefault} className="shrink-0" />
    </span>
  );
}

export default function Tabs({
  className,
  tabItems,
  actionIcon,
  showWhenSingle = false
}: PropsWithChildren<TabsProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useAmplitude();
  const flags = useFlags();
  const navigationPreferencesOn = flags?.["backoffice-navigation-preferences"] === true;
  const activeItem = tabItems?.find((item) => isTabActive(location.pathname, item.link, item.active));
  const activeTab = (activeItem?.link ? tabLocation(String(activeItem.link)) : null) ?? tabLocation(location.pathname);
  const applyGroup = activeTab && canApplyStoredDefaultTab(activeTab.group) ? activeTab.group : undefined;
  const [savedSlug, setSavedSlug] = useState(() => (applyGroup ? getDefaultTab(applyGroup) : undefined));

  useEffect(() => {
    if (!applyGroup) {
      setSavedSlug(undefined);
      return;
    }
    setSavedSlug(getDefaultTab(applyGroup));
  }, [applyGroup, activeTab?.slug]);

  // Don't render tabs if there's only one or zero tab items
  if (!tabItems || (!showWhenSingle && tabItems.length <= 1)) {
    return null;
  }

  const renderedTabs = tabItems.map(
    ({ label, active, onClick, link, badgeValue, badgeColor, iconName, icon, iconOnly, testId }, i) => {
      const isActive = isTabActive(location.pathname, link, active);
      const tabClassName = isActive ? "text-body-large-bold" : "text-body-large-semibold";
      const shouldShowBadge = badgeValue !== undefined || badgeColor !== undefined;
      const showIcon = Boolean(iconName || icon);
      const showLabel = !iconOnly || !showIcon;
      const itemTab = link ? tabLocation(String(link)) : null;
      const canSaveDefaultTab = navigationPreferencesOn && Boolean(itemTab && canApplyStoredDefaultTab(itemTab.group));
      const itemIsSavedDefault = Boolean(itemTab && savedSlug === itemTab.slug);
      const atTabRoot = isExactTabPath(location.pathname, link);
      const defaultTabMarker =
        isActive && canSaveDefaultTab && showLabel ? (
          <DefaultTabMarker isSavedDefault={itemIsSavedDefault} label={label} showActionTitle={atTabRoot} />
        ) : null;
      const tabTitle = defaultTabMarker && atTabRoot ? defaultTabTitle(itemIsSavedDefault, label) : undefined;

      // md-primary-tab stacks icon+label at 64px unless inlineIcon is set. Mixing
      // that with a label-only tab (home Roster) lifts the indicator off the baseline.
      return (
        <LitPrimaryTab
          key={i}
          active={isActive}
          className={`${tabClassName} shrink-0`}
          onClick={() => {
            if (canSaveDefaultTab && isActive && atTabRoot && itemTab) {
              if (savedSlug === itemTab.slug) {
                clearDefaultTab(itemTab.group);
                if (getDefaultTab(itemTab.group) === null) {
                  trackDefaultTabCleared(trackEvent, [itemTab.group], {
                    source: "tab_bar",
                    tabSlug: itemTab.slug
                  });
                  setSavedSlug(null);
                }
              } else {
                setDefaultTab(itemTab.group, itemTab.slug);
                if (getDefaultTab(itemTab.group) === itemTab.slug) {
                  trackDefaultTabSaved(trackEvent, {
                    navigationGroup: itemTab.group,
                    tabSlug: itemTab.slug,
                    previousTabSlug: savedSlug
                  });
                  setSavedSlug(itemTab.slug);
                }
              }
              return;
            }
            if (onClick) {
              onClick();
            } else if (link) {
              navigate(link);
            }
          }}
          data-testid={testId ?? "tab"}
          hasIcon={showIcon}
          iconOnly={Boolean(iconOnly && showIcon)}
          inlineIcon
          aria-label={tabTitle ? `${label}. ${tabTitle}` : iconOnly ? label : undefined}
          title={tabTitle}
        >
          {showIcon ? (
            <span slot="icon" className="relative flex items-center justify-center overflow-visible">
              {icon ?? (iconName ? <Icon name={iconName} size={20} className="shrink-0" /> : null)}
              {!showLabel && shouldShowBadge ? (
                <span className="pointer-events-none absolute -right-1.5 -top-1.5">
                  <Badge value={badgeValue} color={badgeColor} />
                </span>
              ) : null}
            </span>
          ) : null}
          {showLabel ? (
            <span className="flex items-center gap-1">
              {label}
              {shouldShowBadge ? <Badge value={badgeValue} color={badgeColor} /> : null}
              {defaultTabMarker}
            </span>
          ) : null}
        </LitPrimaryTab>
      );
    }
  );

  return (
    <div
      className={`mt-6 flex min-w-0 border-b border-extended-pgadarknavy-brand-color ${
        actionIcon ? "items-end" : "items-center overflow-x-auto"
      } ${className}`}
    >
      {actionIcon ? (
        <>
          <div className="flex min-w-0 flex-1 items-center overflow-x-auto">{renderedTabs}</div>
          <div className="ml-auto shrink-0 pb-6 pl-4" data-testid="index-tab-bar-action-slot">
            {actionIcon}
          </div>
        </>
      ) : (
        renderedTabs
      )}
    </div>
  );
}
